import { store } from '@/store';
import { BASE_URL } from '@/utils/constants/apiEndpoints.ts';

import { orgExtrasBase } from '@/services/emrAccessUsers';

export interface ScheduleLetterEligibility {
  patientId: string;
  eligible: boolean;
  reason: string | null;
  lastSentAt: string | null;
  eligibleOn: string | null;
  postgridContactId: string | null;
  scheduleLetterId: string | null;
}

export interface ScheduleLetterBody {
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  practiceName: string;
  templateId: string;
  isRemoteMonitored: true;
  scheduleDates: string[];
  salesforceAccountId: string;
  fromContactId: string;
}

export interface ScheduleLetterPatient {
  givenName?: string | null;
  familyName?: string | null;
  isRemoteMonitored?: boolean | null;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
  } | null;
  clinic?: { name?: string | null } | null;
}

function authHeaders(json = false): HeadersInit {
  const headers: Record<string, string> = {};
  const token = store.getState().auth.token;
  if (token) headers.Authorization = `Bearer ${token}`;
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

function text(value?: string | null): string {
  return (value || '').trim();
}

/** YYYY-MM-DD from a schedule timestamp, without shifting the calendar day. */
export function scheduleCalendarDay(value?: string | null): string | null {
  const match = text(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  const real =
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  return real ? `${match[1]}-${match[2]}-${match[3]}` : null;
}

export function localCalendarDay(today = new Date()): string {
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
}

export function formatScheduleDay(ymd: string): string {
  const [year, month, day] = ymd.split('-');
  return `${month}-${day}-${year}`;
}

/** The next four dates from today on, one per day, as MM-DD-YYYY. Older dates are dropped. */
export function upcomingScheduleDates(
  values: Array<string | null | undefined>,
  today = new Date()
): string[] {
  const start = localCalendarDay(today);
  return [
    ...new Set(
      values.map(scheduleCalendarDay).filter((day): day is string => Boolean(day && day >= start))
    )
  ]
    .sort()
    .slice(0, 4)
    .map(formatScheduleDay);
}

const SCHEDULE_PAGE_SIZE = 100;
const MAX_SCHEDULE_PAGES = 10;

/**
 * Read Scheduled and Pending rows until four dates from today onward are found.
 * The scheduled filter still includes older rows for a while, and those come back first.
 */
export async function loadUpcomingScheduleDates(
  loadPage: (page: number, limit: number) => Promise<Array<string | null | undefined>>,
  today = new Date()
): Promise<string[]> {
  const collected: Array<string | null | undefined> = [];
  for (let page = 1; page <= MAX_SCHEDULE_PAGES; page += 1) {
    const rows = await loadPage(page, SCHEDULE_PAGE_SIZE);
    collected.push(...rows);
    const upcoming = upcomingScheduleDates(collected, today);
    if (upcoming.length >= 4 || rows.length < SCHEDULE_PAGE_SIZE) return upcoming;
  }
  return upcomingScheduleDates(collected, today);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** Clinic letter settings. The full settings response is the organization itself. */
function clinicLetters(payload: unknown): Record<string, unknown> | null {
  const root = asRecord(payload);
  const organization = asRecord(root?.organization) ?? root;
  const settings = asRecord(organization?.settings);
  return asRecord(settings?.letters);
}

/** The clinic schedule template, when correspondence and that letter are both on. */
export function clinicScheduleTemplateId(payload: unknown): string {
  const letters = clinicLetters(payload);
  if (!letters) return '';
  if (letters.isEnabled !== true) return '';
  const templates = letters.templates;
  if (!Array.isArray(templates)) return '';
  const row = templates.find((item) => asRecord(item)?.letterType === 'schedule');
  const letter = asRecord(row);
  if (letter?.isEnabled !== true) return '';
  const raw = letter.postGridTemplateId;
  return typeof raw === 'string' ? raw.trim() : '';
}

export function scheduleLetterAvailable(
  eligibility: ScheduleLetterEligibility | null | undefined,
  isRemoteMonitored?: boolean | null
): boolean {
  return Boolean(eligibility?.eligible && isRemoteMonitored === true);
}

export function buildScheduleLetterBody(
  patient: ScheduleLetterPatient,
  templateId: string,
  scheduleDates: string[]
): { body: ScheduleLetterBody } | { message: string } {
  if (patient.isRemoteMonitored !== true) {
    return { message: 'This patient is not remote monitored.' };
  }
  const firstName = text(patient.givenName);
  const lastName = text(patient.familyName);
  const addressLine1 = text(patient.address?.line1);
  const city = text(patient.address?.city);
  const state = text(patient.address?.state);
  const postalCode = text(patient.address?.postalCode);
  const practiceName = text(patient.clinic?.name);
  if (!firstName || !lastName || !addressLine1 || !city || !state || !postalCode) {
    return { message: 'The mailing address on the chart is incomplete.' };
  }
  if (!templateId.trim()) {
    return { message: 'This clinic does not have a schedule letter template.' };
  }
  if (scheduleDates.length !== 4) {
    return { message: 'This patient needs four upcoming schedule dates.' };
  }
  return {
    body: {
      firstName,
      lastName,
      addressLine1,
      addressLine2: text(patient.address?.line2),
      city,
      state,
      postalCode,
      practiceName,
      templateId: templateId.trim(),
      isRemoteMonitored: true,
      scheduleDates,
      salesforceAccountId: '',
      fromContactId: ''
    }
  };
}

export function scheduleLetterRefusalMessage(payload: unknown): string {
  const body =
    payload && typeof payload === 'object'
      ? (payload as { reason?: string; message?: string; eligibleOn?: string })
      : {};
  if (body.reason === 'within_30_days') {
    const day = body.eligibleOn?.slice(0, 10);
    return day
      ? `A schedule letter was already sent. Another can be sent on ${formatScheduleDay(day)}.`
      : 'A schedule letter was already sent in the last 30 days.';
  }
  if (body.reason === 'not_remote_monitored') return 'This patient is not remote monitored.';
  return text(body.message) || 'The schedule letter was not sent.';
}

function eligibilityFrom(payload: unknown, patientId: string): ScheduleLetterEligibility | null {
  if (!payload || typeof payload !== 'object') return null;
  const row = payload as Partial<ScheduleLetterEligibility>;
  if (typeof row.eligible !== 'boolean') return null;
  return {
    patientId: row.patientId || patientId,
    eligible: row.eligible,
    reason: row.reason ?? null,
    lastSentAt: row.lastSentAt ?? null,
    eligibleOn: row.eligibleOn ?? null,
    postgridContactId: row.postgridContactId ?? null,
    scheduleLetterId: row.scheduleLetterId ?? null
  };
}

export async function loadScheduleLetterEligibility(
  patientId: string
): Promise<ScheduleLetterEligibility | null> {
  const extras = orgExtrasBase();
  if (!extras || !patientId) return null;
  const response = await fetch(`${extras}/patients/${patientId}/schedule-letter`, {
    headers: authHeaders()
  });
  if (!response.ok) return null;
  return eligibilityFrom(await response.json(), patientId);
}

const clinicTemplateIds = new Map<string, Promise<string>>();

async function fetchClinicScheduleTemplateId(organizationId: string): Promise<string> {
  const base = String(BASE_URL || '').replace(/\/$/, '');
  if (!base || !organizationId) return '';
  const response = await fetch(`${base}/organizations/${organizationId}/settings/full`, {
    headers: authHeaders()
  });
  if (!response.ok) throw new Error('organization unavailable');
  return clinicScheduleTemplateId(await response.json());
}

/** One organization read per clinic. A failed read is not kept, so the next chart can try again. */
export function loadClinicScheduleTemplateId(organizationId: string): Promise<string> {
  if (!organizationId) return Promise.resolve('');
  const cached = clinicTemplateIds.get(organizationId);
  if (cached) return cached;
  const pending = fetchClinicScheduleTemplateId(organizationId).catch(() => {
    clinicTemplateIds.delete(organizationId);
    return '';
  });
  clinicTemplateIds.set(organizationId, pending);
  return pending;
}

export function scheduleLetterSendOutcome(
  ok: boolean,
  payload: unknown
): { sent: true } | { sent: false; message: string; reason: string | null } {
  const reason =
    payload &&
    typeof payload === 'object' &&
    typeof (payload as { reason?: unknown }).reason === 'string'
      ? (payload as { reason: string }).reason
      : null;
  if (
    ok &&
    payload &&
    typeof payload === 'object' &&
    (payload as { sent?: boolean }).sent === true
  ) {
    return { sent: true };
  }
  return { sent: false, message: scheduleLetterRefusalMessage(payload), reason };
}

export async function sendScheduleLetter(
  patientId: string,
  body: ScheduleLetterBody
): Promise<{ sent: true } | { sent: false; message: string; reason: string | null }> {
  const extras = orgExtrasBase();
  if (!extras)
    return { sent: false, message: 'Schedule letters are not available in this environment.' };
  const response = await fetch(`${extras}/patients/${patientId}/schedule-letter`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(body)
  });
  const payload = await response.json().catch(() => ({}));
  return scheduleLetterSendOutcome(response.ok, payload);
}
